import { Liquidity, RangeType } from "@perp/sdk-curie"
import { Big } from "big.js"
import { useMemo, useState } from "react"
import { useMarket } from "sdk-react/markets/useMarket"
import { usePerpInitializedEffect } from "sdk-react/perpetualProtocol/usePerpInitializedEffect"
import { Handlers } from "sdk-react/tools/useHandlers"

interface ILiquidityDetail {
    rangeType?: RangeType
    amountQuote?: Big
    amountBase?: Big
    pendingFee?: Big
}

export function useLiquidityDetail(liquidity: Liquidity) {
    const { marketPrice } = useMarket(liquidity.market.tickerSymbol)

    const [{ amountBase, amountQuote, rangeType, pendingFee }, setState] = useState<ILiquidityDetail>({})
    const { getHandlers } = Handlers.useContainer()
    const { onError } = getHandlers()

    usePerpInitializedEffect(() => {
        // TODO: [429 issue] check if we can reduce contract calls
        const unsubscribeUpdated = liquidity.on("updated", async (liquidityNext: Liquidity) => {
            try {
                const [rangeType, { amountBase, amountQuote }, pendingFee] = await Promise.all([
                    liquidityNext.getRangeType(),
                    liquidityNext.getLiquidityAmounts(),
                    liquidityNext.getPendingFee(),
                ])
                setState({
                    rangeType,
                    amountBase,
                    amountQuote,
                    pendingFee,
                })
            } catch (error: any) {
                onError(error)
            }
        })

        const unsubscribeError = liquidity.on("updateError", ({ error }) => {
            onError(error)
        })

        return () => {
            unsubscribeUpdated()
            unsubscribeError()
        }
    }, [liquidity, onError])

    const amountBaseAsQuote = useMemo(() => {
        if (!amountBase || !marketPrice) {
            return
        }
        return amountBase.mul(marketPrice)
    }, [amountBase, marketPrice])

    const amountTotalAsQuote = useMemo(() => {
        if (!amountQuote || !amountBaseAsQuote) {
            return
        }
        return amountQuote.add(amountBaseAsQuote)
    }, [amountBaseAsQuote, amountQuote])

    return {
        rangeType,
        amountBaseAsQuote,
        amountBase,
        amountQuote,
        amountTotalAsQuote,
        pendingFee,
    }
}
